import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useHistory } from "react-router-dom";
import {
  Box,
  Flex,
  Button,
  FormControl,
  FormLabel,
  Input,
  Text,
  useToast,
  Heading,
  useColorModeValue,
} from "@chakra-ui/react";
import config from "config";

function ResetPassword() {
  const { token } = useParams(); // Retrieve the reset token from the URL params
  const history = useHistory();
  const toast = useToast();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    if (!token) {
      toast({
        title: "Error",
        description: "Invalid or missing reset token.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      history.push("/login"); // Redirect to login if no token
    }
  }, [token, history, toast]);

  const handleResetPassword = async () => {
    setIsSubmitting(true);
    setError(null);
    setSuccessMessage(null);

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await axios.post(`${config.API_URL}/auth/password-reset/${token}`, {
        new_password: newPassword,
      });

      if (response.status === 200) {
        setSuccessMessage("Your password has been successfully reset.");
        toast({
          title: "Success",
          description: "Your password has been successfully reset.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        history.push("/login"); // Redirect to login page after successful reset
      }
    } catch (error) {
      setError("Failed to reset password. Please try again.");
      toast({
        title: "Error",
        description: "Failed to reset password.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Color mode values
  const formBg = useColorModeValue("white", "gray.800");
  const formColor = useColorModeValue("gray.800", "white");
  const buttonColor = useColorModeValue("teal", "teal");
  const errorColor = useColorModeValue("red.500", "red.300");
  const successColor = useColorModeValue("green.500", "green.300");

  return (
    <Flex align="center" justify="center" height="100vh" bg={useColorModeValue("gray.100", "gray.900")}>
      <Box bg={formBg} color={formColor} p="6" rounded="md" shadow="lg" width="100%" maxW="400px">
        <Heading as="h3" size="lg" mb="4" textAlign="center">
          Reset Your Password
        </Heading>
        <FormControl>
          <FormLabel>New Password</FormLabel>
          <Input
            type="password"
            placeholder="Enter new password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            mb="4"
          />
          <FormLabel>Confirm Password</FormLabel>
          <Input
            type="password"
            placeholder="Confirm your new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            mb="4"
          />
          {error && <Text color={errorColor}>{error}</Text>}
          {successMessage && <Text color={successColor}>{successMessage}</Text>}
          <Button
            colorScheme={buttonColor}
            width="100%"
            onClick={handleResetPassword}
            isLoading={isSubmitting}
            isDisabled={newPassword !== confirmPassword || !newPassword || !confirmPassword}
          >
            Reset Password
          </Button>
        </FormControl>
      </Box>
    </Flex>
  );
}

export default ResetPassword;
